import type { ValuesPerEnvironment, Environment } from './configEnvironment';
import { extractConfigValuesBasedOnEnvironment } from './configEnvironment';

export interface Config {
  amplitudeExperimentApiKey: string;
  amplitudeExperimentFlagsServer: string;
  amplitudeExperimentServer: string;
  amplitudeAnalyticsApiUrl: string;
  amplitudeAnalyticsApiKey: string;
  amplitudeTravelServiceServerApiKey: string;
  awsWafCaptchaApiKey: string;
  carbonBasePath: string;
  environment: Environment;
  firebaseEventsRoot: string;
  googleMapsApiKey: string;
  intercomFeedbackSurveyId: string;
  locationServiceUri: string;
  microsoftClarityEnabled: boolean;
  segmentApiHost: string;
  segmentCdnUrl: string;
  segmentKey: string;
  stripeKey: string;
  stripePaymentMethodConfig: string;
  stripeTravelAccountId: string;
  stripeTicketingKey: string;
  stripeTravelKey: string;
}

const config: ValuesPerEnvironment<Config> = {
  // This AWS WAF CAPTCHA key is valid only for *.withjoy.com domains. It will not work on localhost.
  awsWafCaptchaApiKey:
    'x3b0iGT18JlgV2YSvFyatr49VoMM72ZmSrN/Vd/ofO7ZWAoZziNqucXTtrWYVZ4qSoNwmD4ConCNSANv07OHDKXQgdg5RUfB6OjpYilX5lRn0+bERQdMGlvOiMjN8/mB6Qoc31LqRq0/e0Yy7TegIsjWNtuEWXRTEy44hriOHhzi1g7CjCQTiZNBgORWiVq5xqhmrGgigYSdNpSJ9VJFZYgjP1AcFUI28QD+sXRjjGBacJcg88r7I62tsuSg1OfHIPEj73eVvaYQsqe66jCXyrQVCQA+FQ8Zknuj3jupLH1b9hTpClsRkOcXIyR4FbqWl42j4oyzOuDiZmBz4Iu+xqrA0z+IVzgVf6xRMt7O/6PvJBOu1ZhyHmWNdPM0MDaosVQMb38VEzw+CSNKNqS5fIdVeUYY0JwPaFcgIZUNXiXiQsUi8rJM7Ah0rmQJzhEsSstcdh6z+A865Xnx2bYCIJWKOlnz00Op43gyfGMW/gEFNby6+9LvsKSNGGr8VZAGt6Sy2MMef8SLyGY2faIFCCpJNS12fMPaUYK1XNoXcmE1hQXvRqqr3G5DQcFkA0/1H1gKIjA7CrxVtTNhcCpWrr09REFLPb5cTdXRrvut91JkCJrabYEIZOfBMjf/Qh3Z2c9sDmNOyPSazODj0HGwuuE9UssbPyRlMpeYDDIzw3U=_1_1',
  amplitudeExperimentApiKey: {
    dev: 'client-VBfZGm3gUxArOnypZ2EvbaibP8F9niEk',
    stage: 'client-VBfZGm3gUxArOnypZ2EvbaibP8F9niEk',
    prod: 'client-duj6ZiTMbFQqhVN4EDm9OgNyCrWtMqU0',
  },
  amplitudeTravelServiceServerApiKey: {
    dev: 'server-DZKMT6fOl3qYJByO5r7JJ2FoMubd9HTI',
    stage: 'server-DZKMT6fOl3qYJByO5r7JJ2FoMubd9HTI',
    prod: 'server-cOdLaQRu0izhRL5GRA3gpZPKDT0xqU2R',
  },
  amplitudeExperimentFlagsServer: 'https://p.withjoy.com/ampflags',
  amplitudeExperimentServer: 'https://p.withjoy.com/ampexp',
  amplitudeAnalyticsApiUrl: 'https://p.withjoy.com/ampapi/2/httpapi',
  // Client-side Amplitude API keys - these are public and safe to include in client bundle
  amplitudeAnalyticsApiKey: {
    dev: '4febc1f78561c9a1c4293908f575ec4c',
    stage: '4febc1f78561c9a1c4293908f575ec4c',
    prod: '2f8e78a3989d79b744563349220287ab',
  },
  carbonBasePath: 'finola',
  environment: {
    dev: 'dev',
    stage: 'stage',
    prod: 'prod',
  },
  intercomFeedbackSurveyId: {
    dev: '40137453',
    stage: '40137453',
    prod: '40068519',
  },
  googleMapsApiKey: 'AIzaSyB9ePoqHL2N2xqOhy2H_7vjDokVDIq8-fE',
  microsoftClarityEnabled: {
    dev: false,
    stage: false,
    prod: true,
  },
  segmentApiHost: 'p.withjoy.com/segapi',
  segmentCdnUrl: 'https://p.withjoy.com/segcdn',
  segmentKey: {
    dev: '2xZJ98aly8fmfFDB9msDQlXsyVGVpUby',
    stage: '2xZJ98aly8fmfFDB9msDQlXsyVGVpUby',
    prod: 'oHWqP4BrVeBDl1FxCTC45FfLZ0qnySFw',
  },
  locationServiceUri: {
    dev: 'https://dev.withjoy.com/services/location',
    stage: 'https://stage.withjoy.com/services/location',
    prod: 'https://withjoy.com/services/location',
  },
  firebaseEventsRoot: 'https://joyevents.firebaseio.com',
  stripeKey: {
    dev: 'pk_test_51H3QsXL8ZJR6ve63et33srSAbUrUyQIRCDm0mRYlqqvMfmS1QFUfDRGED4GcxFCtaOLppAerI64im8RHX2JGCdZh00hkzQzAaT',
    stage: 'pk_test_51H3QsXL8ZJR6ve63et33srSAbUrUyQIRCDm0mRYlqqvMfmS1QFUfDRGED4GcxFCtaOLppAerI64im8RHX2JGCdZh00hkzQzAaT',
    prod: 'pk_live_51H3QsXL8ZJR6ve63MRanGXzlfyoY58D6iTE6gASqViOXIDR8qvRI6IhDkI20Xx5hsRCogiUf0ViUosP3CWjvvgeM00vHhh15E8',
  },
  stripeTicketingKey: {
    dev: 'pk_test_51T4hIGL47QakH9SSHt1oGTex1v1BF7PbvsY906eGawxYXM10FOEzjOj52yKh1ZSS5BTqTfgWOut7SzfKIo7TlZiE00XG9nmfLN',
    stage: 'pk_test_51T4hIGL47QakH9SSHt1oGTex1v1BF7PbvsY906eGawxYXM10FOEzjOj52yKh1ZSS5BTqTfgWOut7SzfKIo7TlZiE00XG9nmfLN',
    prod: 'pk_live_51ROjpwPqMXmkyqCSHX1S6IUn7duOWUGs6ZvCIRmbu9ylD12YpmQu9zY5gMmnK0pwvsG96QdYdtCPrzyZMFDnGehf00I3fj536C',
  },
  stripeTravelAccountId: {
    dev: 'acct_1RiMWuPrIeLJkIWO',
    stage: 'acct_1RiMWuPrIeLJkIWO',
    prod: 'acct_1QiKYjPufeHoJl6K',
  },
  stripeTravelKey: {
    dev: 'pk_test_51RiMWuPrIeLJkIWOI9dATUokfmprUAmSF8fmSPc9rLUS3vWh5HALHx2Ua0r1xKNKqNcFAMLDlcNeOlMlH2Zaihkj00a4fGdDDz',
    stage: 'pk_test_51RiMWuPrIeLJkIWOI9dATUokfmprUAmSF8fmSPc9rLUS3vWh5HALHx2Ua0r1xKNKqNcFAMLDlcNeOlMlH2Zaihkj00a4fGdDDz',
    prod: 'pk_live_51QiKYjPufeHoJl6KFddqxucd13YKenyynuA5sX3RBuAwgtEf7i3zUH7IiqwxWyWhpDo7ZtewODgVM6Rp2sAE8x7x00JPhLQzyP',
  },
  stripePaymentMethodConfig: {
    dev: 'pmc_1QyxqEL8ZJR6ve63QgajiVuY',
    stage: 'pmc_1QyxqEL8ZJR6ve63QgajiVuY',
    prod: 'pmc_1R1x0AL8ZJR6ve63UVhuL818',
  },
};

let builtConfig: Config;
export function getConfig(): Config {
  if (!builtConfig) {
    builtConfig = extractConfigValuesBasedOnEnvironment(config);
  }

  return builtConfig;
}
